import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import apis from "../../../context/api";
import { Checkbox, Typography } from "@mui/material";
import MDTypography from "components/MDTypography";
import { clampSellingPrice } from "../../../utils/pricing";
import { can } from "../../../context/permissions";
import AddressSelect from "../../../components/AddressSelect";
import AddressModal from "../../../components/AddressModal";

// eslint-disable-next-line react/prop-types
function QuotationEdit({ id, onSuccess, onCancel }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [grandTotal, setGrandTotal] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [showCustomerFields, setShowCustomerFields] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [marginPercentage, setMarginPercentage] = useState(0);

  const [newCustomerFields, setNewCustomerFields] = useState({
    name: "",
    email: "",
    mobile: "",
    storeName: "",
    address: "",
    includeAddress: false,
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });
  const [comboData, setComboData] = useState({
    name: "",
    shortName: "",
    items: [],
    customerId: null,
    discount: "",
    userId: null,
    selectedItems: [],
  });
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [removedItems, setRemovedItems] = useState([]);
  const [quotationType, setQuotationType] = useState("standard");
  const [customDescriptions, setCustomDescriptions] = useState({});
  const [shipToAddressId, setShipToAddressId] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddressMode, setEditingAddressMode] = useState(false);

  useEffect(() => {
    apis
      .getComboAndProducts()
      .then((data) => {
        setProducts(data);
      })
      .catch(() => setError("Failed to load products"));

    apis
      .getCustomers()
      .then((data) => setCustomers(data))
      .catch(() => setError("Failed to load customers"));
  }, []);

  useEffect(() => {
    if (id) {
      apis
        .getQuotationById(id)
        .then((data) => {
          if (data && products.length > 0) {
            const selectedItems = [];
            data.items.forEach((p) => {
              const matchedProduct = products.find((prod) =>
                p.itemType === "product" ? prod.id === p.productId : prod.id === p.comboId
              );
              p.customerProvided = p.customerProvided || 0;
              if (matchedProduct && !selectedItems.some((item) => item.id === matchedProduct.id)) {
                selectedItems.push(matchedProduct);
              }
            });
            data.selectedItems = selectedItems;
            // Set type
            if (selectedItems.length > 0) {
              setQuotationType(selectedItems[0].type === "combo" ? "combo" : "standard");
            }
          } else {
            data.selectedItems = [];
          }
          const descriptions = {};
          (data.items || []).forEach((item) => {
            if (item.customDescription) {
              descriptions[`${item.productId}_${item.comboId || "standard"}`] =
                item.customDescription;
            }
          });
          setCustomDescriptions(descriptions);
          setComboData(data);
          if (data.shipToAddressId) {
            setShipToAddressId(data.shipToAddressId);
          }
          setCustomer(data.customer);
          // Extract marginPercentage and discountAmount from loaded data
          setMarginPercentage(data.marginPercentage || 0);
          setDiscountAmount(data.discountAmount || 0);
          // Ensure calculation includes transport from loaded data
          setTimeout(() => {
            calculateTotal(data.items);
          }, 200);
        })
        .catch(() => {
          setError("Quotation not found");
          setTimeout(() => {
            navigate("/quotation");
          }, 1500);
        });
    } else {
      setNewCustomerFields({
        name: "",
        email: "",
        mobile: "",
        storeName: "",
        address: "",
        includeAddress: false,
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      });
      setComboData({
        name: "",
        shortName: "",
        items: [],
        customerId: null,
        discount: "",
        userId: null,
        selectedItems: [],
      });
      setQuotationType("");
      setMarginPercentage(0);
      setDiscountAmount(0);
    }
  }, [products]);

  const handleChange = (e) => {
    setComboData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleProductSelect = (event, values) => {
    // Determine type based on first selection
    let type = quotationType;
    if (!quotationType && values.length > 0) {
      type = values[0].type === "combo" ? "combo" : "standard";
      setQuotationType(type);
    }

    // If type is standard, prevent adding combos
    if (type === "standard" && values.some((v) => v.type === "combo")) {
      return; // Ignore combo selection
    }

    // If all standard products removed, allow combo selection again
    if (type === "standard" && values.every((v) => v.type !== "product")) {
      setQuotationType("");
      type = "";
    }
    // If all combos removed, allow standard selection again
    if (type === "combo" && values.every((v) => v.type !== "combo")) {
      setQuotationType("");
      type = "";
    }

    // Extract marginPercentage from selected combo
    const comboWithMargin = values.find((v) => v.type === "combo");
    if (comboWithMargin) {
      setMarginPercentage(comboWithMargin.marginPercentage || 0);
    } else if (type === "combo") {
      setMarginPercentage(0);
    }

    // Remove removedItems for combos that are no longer selected
    const selectedComboIds = values.filter((v) => v.type === "combo").map((c) => c.id);
    setRemovedItems((prev) =>
      prev.filter((removed) => !removed.comboId || selectedComboIds.includes(removed.comboId))
    );

    // Build items list by expanding combos into products while preserving edits.
    let expandedItems = [];
    values.forEach((option) => {
      if (option.type === "combo") {
        option.products?.forEach((prod) => {
          const existingItem = comboData.items.find(
            (item) => item.productId === prod.id && item.comboId === option.id
          );
          expandedItems.push({
            ...existingItem,
            productId: prod.id,
            quantity: existingItem?.quantity ?? prod.quantity ?? 1,
            sellingPrice: existingItem?.sellingPrice ?? prod.sellingPrice ?? 0,
            lowestSellPrice: prod.lowestSellPrice || 0,
            comboId: option.id,
            comboName: option.name,
            itemType: "combo",
          });
        });
      } else {
        const existingItem = comboData.items.find(
          (item) => item.productId === option.id && item.comboId === null
        );
        expandedItems.push({
          ...existingItem,
          productId: option.id,
          quantity: existingItem?.quantity ?? 1,
          sellingPrice: existingItem?.sellingPrice ?? option.sellingPrice ?? 0,
          lowestSellPrice: option.lowestSellPrice || 0,
          comboId: null,
          comboName: null,
          itemType: "product",
        });
      }
    });

    // Filter out removed combo items
    expandedItems = expandedItems.filter(
      (item) =>
        !removedItems.some(
          (removed) => removed.productId === item.productId && removed.comboId === item.comboId
        )
    );

    setComboData((prev) => ({
      ...prev,
      selectedItems: values,
      items: expandedItems,
    }));

    calculateTotal(expandedItems);
  };

  // Store the raw input while typing (allow empty so the user can clear "1" and type a new number).
  // The value is normalized to a minimum of 1 on blur.
  const handleQuantityChange = (productId, quantity, comboId) => {
    const updatedItems = comboData.items.map((item) =>
      item.productId === productId && item.comboId === comboId ? { ...item, quantity } : item
    );

    setComboData((prev) => ({ ...prev, items: updatedItems }));
    calculateTotal(updatedItems);
  };

  const handleQuantityBlur = (productId, comboId) => {
    const updatedItems = comboData.items.map((item) => {
      if (item.productId === productId && item.comboId === comboId) {
        const q = parseInt(item.quantity, 10);
        return { ...item, quantity: Number.isNaN(q) || q < 1 ? 1 : q };
      }
      return item;
    });

    setComboData((prev) => ({ ...prev, items: updatedItems }));
    calculateTotal(updatedItems);
  };
  const handleTransportChange = (value) => {
    setComboData((prev) => ({ ...prev, transport: value }));
  };
  // While typing, keep whatever the user enters (including empty) so the field
  // stays editable and never becomes NaN. The lowestSellPrice floor is enforced
  // on blur instead of on every keystroke.
  const handleSellingPriceChange = (productId, sellingPrice, comboId) => {
    const updatedItems = comboData.items.map((item) =>
      item.productId === productId && item.comboId === comboId ? { ...item, sellingPrice } : item
    );
    setComboData((prev) => ({ ...prev, items: updatedItems }));
    calculateTotal(updatedItems);
  };

  // On blur, normalize to a safe number and enforce the lowestSellPrice floor.
  const handleSellingPriceBlur = (productId, rawValue, comboId) => {
    const updatedItems = comboData.items.map((item) =>
      item.productId === productId && item.comboId === comboId
        ? { ...item, sellingPrice: clampSellingPrice(rawValue, item.lowestSellPrice) }
        : item
    );
    setComboData((prev) => ({ ...prev, items: updatedItems }));
    calculateTotal(updatedItems);
  };
  const handleCheckBoxChange = (productId, value, comboId) => {
    const updatedItems = comboData.items.map((item) => {
      if (item.productId === productId && item.comboId === comboId) {
        return { ...item, customerProvided: value };
      }
      return item;
    });
    setComboData((prev) => ({ ...prev, items: updatedItems }));
  };

  const calculateComboItemsWithMargin = (items, margin) => {
    return items.map((item) => {
      const product = products.find((p) => p.id === item.productId);
      const buyPrice = product?.buyPrice || 0;
      const sellingPrice = buyPrice * (1 + margin / 100);
      return { ...item, sellingPrice, buyPrice };
    });
  };

  const removeProduct = (productId, comboId) => {
    let updatedItems = comboData.items.filter(
      (item) => !(item.productId === productId && item.comboId === comboId)
    );
    setRemovedItems((prev) => [...prev, { productId, comboId }]);

    // If the product removed belongs to a combo, check if combo should still remain
    if (comboId) {
      const comboStillHasItems = updatedItems.some((item) => item.comboId === comboId);
      if (!comboStillHasItems) {
        // Remove combo from selectedItems
        const updatedSelectedItems = comboData.selectedItems.filter(
          (sel) => !(sel.type === "combo" && sel.id === comboId)
        );

        setComboData((prev) => ({
          ...prev,
          selectedItems: updatedSelectedItems,
          items: updatedItems,
        }));

        calculateTotal(updatedItems);
        return;
      }
    } else {
      // If all standard products removed, reset type
      if (updatedItems.every((item) => item.comboId)) {
        setQuotationType("");
      }
    }

    setComboData((prev) => ({
      ...prev,
      items: updatedItems,
    }));

    calculateTotal(updatedItems);
  };

  const handleCustomerChange = (event, value) => {
    setCustomer(value);
    setShowCustomerFields(false);

    if (!value) {
      setNewCustomerFields({
        name: "",
        email: "",
        mobile: "",
        storeName: "",
        address: "",
        includeAddress: false,
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      });
    }

    // Auto-populate default ship-to address
    if (value && value.defaultShipToAddressId) {
      setShipToAddressId(value.defaultShipToAddressId);
    } else {
      setShipToAddressId(null);
    }
  };
  const calculateTotal = (updatedItems, manual = false) => {
    let total = 0;
    let totalTaxAmount = 0;

    if (quotationType === "combo") {
      // Calculate subtotal from items with margin-based pricing
      const itemsWithMargin = calculateComboItemsWithMargin(updatedItems, marginPercentage);
      const subtotal = itemsWithMargin.reduce((sum, item) => {
        const quantity = item.quantity ?? 1;
        const price = parseFloat(item.sellingPrice) || 0;
        return sum + price * quantity;
      }, 0);

      const transport = parseFloat(comboData.transport || 0);
      const discount = parseFloat(discountAmount || 0);

      // Calculate taxable amount: subtotal - discount + transport
      const taxableAmount = subtotal - discount + transport;

      // GST is 18% on taxable amount (including transport)
      totalTaxAmount = taxableAmount * 0.18;

      // Total with GST
      total = taxableAmount + totalTaxAmount;
    } else {
      // Standard: GST per product, transport included in taxable
      const transport = parseFloat(comboData.transport || 0);
      let subtotal = 0;
      let taxAmount = 0;

      updatedItems.forEach((item) => {
        const product = products.find((p) => p.id === item.productId);
        const price = parseFloat(item.sellingPrice) || product?.sellingPrice || 0;
        const quantity = item.quantity ?? 1;
        const gst = product?.gst ?? 0;
        const itemSubtotal = price * quantity;
        subtotal += itemSubtotal;
        taxAmount += itemSubtotal * (gst / 100);
      });

      // Add transport to taxable amount for GST
      const taxableAmount = subtotal + transport;
      totalTaxAmount = taxAmount + transport * 0.18; // Tax on transport at standard 18%
      total = taxableAmount + totalTaxAmount;
    }

    setGrandTotal(Number(total.toFixed(2)));
    setTotalTax(totalTaxAmount.toFixed(2));
  };

  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomerFields((prev) => ({ ...prev, [name]: value }));
  };
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };
  const validateNewCustomerAddressFields = () => {
    const newErrors = {};
    if (!newCustomerFields.addressLine1.trim())
      newErrors.addressLine1 = "Address Line 1 is required";
    if (!newCustomerFields.city.trim()) newErrors.city = "City is required";
    if (!newCustomerFields.state.trim()) newErrors.state = "State is required";
    if (!newCustomerFields.postalCode.trim()) newErrors.postalCode = "Postal Code is required";
    if (!newCustomerFields.country.trim()) newErrors.country = "Country is required";
    return newErrors;
  };

  // Creates the new customer and, if requested, a shipping address for it in one step.
  // Returns the created (and possibly address-augmented) customer object, or null on
  // validation failure / API error (in which case errors/error state is already set).
  const createNewCustomerWithAddress = async () => {
    const customerErrors = {};
    if (!newCustomerFields.mobile.trim()) customerErrors.mobile = "Mobile  is required";
    if (!newCustomerFields.name.trim()) customerErrors.name = "Name is required";

    const addressErrors = newCustomerFields.includeAddress
      ? validateNewCustomerAddressFields()
      : {};

    const combinedErrors = { ...customerErrors, ...addressErrors };
    if (Object.keys(combinedErrors).length > 0) {
      setErrors(combinedErrors);
      return null;
    }
    setErrors({});

    try {
      const newCust = await apis.createCustomer(newCustomerFields);
      let updatedCustomer = newCust;

      if (newCustomerFields.includeAddress) {
        const customerId = newCust.id || newCust.customerId || newCust.customer_id;
        if (!customerId) {
          throw new Error(
            "Customer ID not found in response. Please check backend response structure."
          );
        }

        const addressData = {
          addressLine1: newCustomerFields.addressLine1,
          addressLine2: newCustomerFields.addressLine2,
          city: newCustomerFields.city,
          state: newCustomerFields.state,
          postalCode: newCustomerFields.postalCode,
          country: newCustomerFields.country,
          type: "shipping",
        };
        const newAddress = await apis.createAddress(customerId, addressData);
        await apis.updateCustomerDefaults(customerId, { defaultShipToAddressId: newAddress.id });

        updatedCustomer = {
          ...newCust,
          addresses: newCust.addresses ? [...newCust.addresses, newAddress] : [newAddress],
          defaultShipToAddressId: newAddress.id,
        };
        setShipToAddressId(newAddress.id);
      }

      setCustomer(updatedCustomer);
      setShowCustomerFields(false);
      setNewCustomerFields({
        name: "",
        email: "",
        mobile: "",
        storeName: "",
        address: "",
        includeAddress: false,
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      });

      return updatedCustomer;
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    let customerId = customer?.id;

    if (!customerId) {
      // Create new customer (and its shipping address, if requested) first
      const newCust = await createNewCustomerWithAddress();
      if (!newCust) return;
      customerId = newCust.id;
    }

    if (!customerId) {
      alert("please select customer before proceeding");
      return;
    }
    if (comboData.items.length == 0) {
      alert("please select product before proceeding");
      return;
    }
    // if (!shipToAddressId) {
    //   alert("Please select a Ship To address");
    //   return;
    // }
    // if (!billToAddressId) {
    //   alert("Please select a Bill To address");
    //   return;
    // }

    const payloadItems = comboData.items.map((item) => ({
      ...item,
      customDescription:
        customDescriptions[`${item.productId}_${item.comboId || "standard"}`] || "",
    }));

    const payload = {
      ...comboData,
      items: payloadItems,
      customerId,
      shipToAddressId,
      discountAmount,
      marginPercentage,
      grandTotal: grandTotal,
      totalAmount: grandTotal,
      tax: totalTax,
    };
    const method = id ? "PUT" : "POST";
    const url = id ? `quotations/${id}` : `quotations`;

    try {
      const response = await apis.quotationCreateUpdate(payload, method, url);
      if (!response.quotationId) throw new Error("Failed to save quotation");

      if (onSuccess) {
        sendInvoice(id || response.quotationId);
        setTimeout(() => {
          onSuccess(response);
        }, 1000);
      }
    } catch (err) {
      setError(err.message);
    }
  };
  const newCustomer = () => {
    setCustomer(null);
    setShowCustomerFields(true);
    setShipToAddressId(null);
    setErrors({});
    setNewCustomerFields({
      name: "",
      email: "",
      mobile: "",
      storeName: "",
      address: "",
      gstn: "",
      includeAddress: false,
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    });
  };
  const sendInvoice = (id) => {
    apis
      .sendAndDownloadInvoice(id)
      .then(({ blob, filename }) => {
        const url = window.URL.createObjectURL(blob);
        const now = new Date();
        const yearMonth = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
        const paddedId = String(id).padStart(4, "0");
        const invoiceNumber = `${yearMonth}-${paddedId}`;
        // Create a temporary link to trigger download
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.download = filename || `invoice_${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url); // Clean up
      })
      .catch((err) => {
        console.error("Data loading error:", err);
      });
  };
  useEffect(() => {
    if (quotationType && comboData.items && comboData.items.length > 0) {
      calculateTotal(comboData.items);
    }
    // eslint-disable-next-line
  }, [quotationType, comboData.transport, comboData.items]);

  const shouldShowDescriptionEditor = (product) => {
    // const hasPermission = can("Product", "canEditProductDescription");
    // if (!hasPermission) return false;

    // For combos: always show if permission exists
    // if (quotationType === "combo") return true;

    // For standard products: only if product allows editing
    return product?.allowEditProductDescription == 1;
  };

  const handleAddressSaved = (newAddress) => {
    // Validate that the address type is shipping (required for Ship To Address)
    if (newAddress.type !== "shipping") {
      setError("Ship To Address must be a shipping type address");
      return;
    }

    // Auto-select the newly created address
    setShipToAddressId(newAddress.id);

    // Update customer addresses list
    setCustomer((prev) => ({
      ...prev,
      addresses: prev.addresses ? [...prev.addresses, newAddress] : [newAddress],
    }));

    setShowAddressModal(false);
    setEditingAddressMode(false);
  };

  const selectedProducts = comboData.selectedItems;
  const groupedItems = comboData.items.reduce((acc, item) => {
    const key = item.comboId ? `${item.comboId}-${item.comboName}` : "standalone";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <MDBox pt={6} pb={6}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={5}>
              <Grid container spacing={2}>
                <Grid item xs={8} md={10}>
                  <Autocomplete
                    options={customers}
                    getOptionLabel={(option) => option.storeName || ""}
                    value={customer}
                    onChange={handleCustomerChange}
                    renderInput={(params) => <TextField {...params} label="Select Customer" />}
                    fullWidth
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </Grid>
                <Grid item xs={2}>
                  <MDButton variant="gradient" color="info" onClick={newCustomer}>
                    New Customer
                  </MDButton>
                </Grid>

                {showCustomerFields && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Name"
                        name="name"
                        value={newCustomerFields.name}
                        onChange={handleNewCustomerChange}
                        fullWidth
                        error={!!errors.name}
                        helperText={errors.name}
                        FormHelperTextProps={{
                          sx: { color: "red" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Email"
                        name="email"
                        type="email"
                        value={newCustomerFields.email}
                        onChange={handleNewCustomerChange}
                        fullWidth
                        // error={!!errors.email}
                        // helperText={errors.email}
                        // FormHelperTextProps={{
                        //   sx: { color: "red" },
                        // }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Mobile"
                        name="mobile"
                        type="number"
                        value={newCustomerFields.mobile}
                        onChange={handleNewCustomerChange}
                        fullWidth
                        error={!!errors.mobile}
                        helperText={errors.mobile}
                        FormHelperTextProps={{
                          sx: { color: "red" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Store Name"
                        name="storeName"
                        value={newCustomerFields.storeName}
                        onChange={handleNewCustomerChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="GST No"
                        name="gstn"
                        value={newCustomerFields.gstn}
                        onChange={handleNewCustomerChange}
                        fullWidth
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Address"
                        name="address"
                        value={newCustomerFields.address}
                        onChange={handleNewCustomerChange}
                        fullWidth
                        multiline
                        rows={2}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <MDBox display="flex" alignItems="center">
                        <Checkbox
                          checked={newCustomerFields.includeAddress}
                          onChange={(e) =>
                            setNewCustomerFields((prev) => ({
                              ...prev,
                              includeAddress: e.target.checked,
                            }))
                          }
                        />
                        <MDTypography
                          variant="button"
                          fontWeight="regular"
                          color="text"
                          sx={{ cursor: "pointer", userSelect: "none" }}
                        >
                          Add shipping address now
                        </MDTypography>
                      </MDBox>
                    </Grid>

                    {newCustomerFields.includeAddress && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <MDInput
                            label="Address Line 1"
                            name="addressLine1"
                            value={newCustomerFields.addressLine1}
                            onChange={handleNewCustomerChange}
                            fullWidth
                            error={!!errors.addressLine1}
                            helperText={errors.addressLine1}
                            FormHelperTextProps={{
                              sx: { color: "red" },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <MDInput
                            label="Address Line 2 (optional)"
                            name="addressLine2"
                            value={newCustomerFields.addressLine2}
                            onChange={handleNewCustomerChange}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <MDInput
                            label="City"
                            name="city"
                            value={newCustomerFields.city}
                            onChange={handleNewCustomerChange}
                            fullWidth
                            error={!!errors.city}
                            helperText={errors.city}
                            FormHelperTextProps={{
                              sx: { color: "red" },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <MDInput
                            label="State"
                            name="state"
                            value={newCustomerFields.state}
                            onChange={handleNewCustomerChange}
                            fullWidth
                            error={!!errors.state}
                            helperText={errors.state}
                            FormHelperTextProps={{
                              sx: { color: "red" },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <MDInput
                            label="Postal Code"
                            name="postalCode"
                            value={newCustomerFields.postalCode}
                            onChange={handleNewCustomerChange}
                            fullWidth
                            error={!!errors.postalCode}
                            helperText={errors.postalCode}
                            FormHelperTextProps={{
                              sx: { color: "red" },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <MDInput
                            label="Country"
                            name="country"
                            value={newCustomerFields.country}
                            onChange={handleNewCustomerChange}
                            fullWidth
                            error={!!errors.country}
                            helperText={errors.country}
                            FormHelperTextProps={{
                              sx: { color: "red" },
                            }}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}

                {customer && (
                  <Grid item xs={12}>
                    <AddressSelect
                      label="Ship To Address"
                      value={shipToAddressId}
                      onChange={(id) => setShipToAddressId(id)}
                      onAddClick={() => {
                        setEditingAddressMode(true);
                        setShowAddressModal(true);
                      }}
                      addresses={
                        customer.addresses
                          ? customer.addresses.filter((a) => a.type === "shipping")
                          : []
                      }
                    />
                  </Grid>
                )}

                {/* {comboData.items.map((item) => {
                  const product = products.find((p) => p.id === item.productId);
                  return (
                    <Grid
                      container
                      spacing={1}
                      p={2}
                      key={item.productId}
                      alignItems="center"
                      mt={1}
                    >
                      <Grid item xs={4} md={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          {product?.productShortName || "Product"}
                        </Typography>
                      </Grid>
                      <Grid item xs={3} md={2}>
                        <MDInput
                          label={product?.productShortName + " quantity" || "Product" + " quantity"}
                          value={item.quantity}
                          onChange={(e) =>
                            handleQuantityChange(item.productId, e.target.value, item.comboId)
                          }
                          onBlur={() => handleQuantityBlur(item.productId, item.comboId)}
                          type="number"
                          inputProps={{ min: 1 }}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={3} md={2}>
                        <MDInput
                          label={product?.productShortName + " price" || "Product" + " price"}
                          value={item.sellingPrice}
                          onChange={(e) =>
                            handleSellingPriceChange(item.productId, parseInt(e.target.value))
                          }
                          type="number"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2} md={1} sx={{ textAlign: "right" }}>
                        <IconButton onClick={() => removeProduct(item.productId)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  );
                })} */}
                {Object.entries(groupedItems).map(([groupKey, items]) => {
                  const isStandalone = groupKey === "standalone";
                  let groupTitle = "";

                  if (quotationType === "combo") {
                    // Always show combo name (even if standard products are also selected)
                    groupTitle = !isStandalone ? `Combo: ${items[0]?.comboName}` : "";
                  } else if (quotationType === "standard") {
                    // Only show "Standalone Products"
                    groupTitle = isStandalone ? "Standalone Products" : "";
                  } else {
                    // Fallback: show both
                    groupTitle = isStandalone
                      ? "Standalone Products"
                      : `Combo: ${items[0]?.comboName}`;
                  }

                  return (
                    <Grid container key={groupKey} mt={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          {groupTitle}
                        </Typography>
                      </Grid>

                      {items.map((item) => {
                        const product =
                          products.find((p) => p.id === item.productId) ||
                          products
                            .flatMap((p) => p.products || [])
                            .find((p) => p.id === item.productId);

                        // Calculate selling price for combos
                        const buyPrice = product?.buyPrice || 0;
                        const sellingPrice =
                          quotationType === "combo"
                            ? buyPrice * (1 + marginPercentage / 100)
                            : item.sellingPrice;

                        return (
                          <Grid
                            container
                            spacing={2}
                            p={2}
                            key={`${item.productId}_${item.comboId || "standalone"}`}
                            alignItems="center"
                            mt={1}
                            sx={{
                              border: "1px solid",
                              borderColor: "grey.300",
                              borderRadius: 2,
                              backgroundColor: "grey.100",
                            }}
                          >
                            <Grid item xs={12} md={3}>
                              <Typography variant="subtitle2">
                                {product?.productName || product?.productShortName || "Product"}
                              </Typography>
                            </Grid>
                            <Grid item xs={5} md={2}>
                              <MDInput
                                label="Quantity"
                                value={item.quantity}
                                onChange={(e) =>
                                  handleQuantityChange(item.productId, e.target.value, item.comboId)
                                }
                                onBlur={() => handleQuantityBlur(item.productId, item.comboId)}
                                type="number"
                                inputProps={{ min: 1 }}
                                fullWidth
                              />
                            </Grid>
                            {quotationType === "combo" ? (
                              <Grid item xs={5} md={3}>
                                <Typography variant="body2" sx={{ fontWeight: "600" }}>
                                  {/* Buy: ₹ {buyPrice} | */}
                                  Sell: ₹ {(sellingPrice * item.quantity).toFixed(2)}
                                </Typography>
                              </Grid>
                            ) : (
                              <Grid item xs={5} md={3}>
                                <MDInput
                                  label="Selling Price"
                                  value={item.sellingPrice ?? ""}
                                  onChange={(e) =>
                                    handleSellingPriceChange(
                                      item.productId,
                                      e.target.value,
                                      item.comboId
                                    )
                                  }
                                  onBlur={(e) =>
                                    handleSellingPriceBlur(
                                      item.productId,
                                      e.target.value,
                                      item.comboId
                                    )
                                  }
                                  type="number"
                                  inputProps={{ min: item.lowestSellPrice || 0 }}
                                  fullWidth
                                />
                              </Grid>
                            )}
                            <Grid item xs={2} md={1} sx={{ textAlign: "right" }}>
                              <IconButton
                                onClick={() => removeProduct(item.productId, item.comboId)}
                                aria-label="delete"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                            {/* <Grid item xs={12} sx={{ mt: 1, pl: 2 }}>
                              <MDTypography variant="caption" color="textSecondary">
                                Base Description: {product?.productDescription || "No description"}
                              </MDTypography>
                            </Grid> */}
                            {shouldShowDescriptionEditor(product) && (
                              <Grid item xs={12} sx={{ mt: 1, pl: 2 }}>
                                <MDInput
                                  fullWidth
                                  multiline
                                  rows={2}
                                  label="Description for this quotation (optional)"
                                  placeholder="Enter custom description for this customer"
                                  value={
                                    customDescriptions[
                                      `${item.productId}_${item.comboId || "standard"}`
                                    ] || ""
                                  }
                                  onChange={(e) =>
                                    setCustomDescriptions((prev) => ({
                                      ...prev,
                                      [`${item.productId}_${item.comboId || "standard"}`]:
                                        e.target.value,
                                    }))
                                  }
                                  variant="outlined"
                                />
                              </Grid>
                            )}
                            {!isStandalone && (
                              <Grid item xs={12} md={3}>
                                <MDBox display="flex" alignItems="center">
                                  <Checkbox
                                    checked={item.customerProvided == 1}
                                    onChange={(e) =>
                                      handleCheckBoxChange(
                                        item.productId,
                                        e.target.checked ? 1 : 0,
                                        item.comboId
                                      )
                                    }
                                  />
                                  <MDTypography
                                    variant="button"
                                    fontWeight="regular"
                                    color="text"
                                    sx={{ cursor: "pointer", userSelect: "none" }}
                                  >
                                    Customer Provided
                                  </MDTypography>
                                </MDBox>
                              </Grid>
                            )}
                          </Grid>
                        );
                      })}
                    </Grid>
                  );
                })}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={products}
                    getOptionLabel={(option) =>
                      option.type === "combo"
                        ? `Combo: ${option.name || option.shortName}`
                        : option.productName || option.productShortName || option.name
                    }
                    value={selectedProducts}
                    onChange={handleProductSelect}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Products or Combos" />
                    )}
                    fullWidth
                    isOptionDisabled={(option) =>
                      quotationType === "standard" && option.type === "combo"
                    }
                  />
                </Grid>
                {/*<Grid item xs={12} sm={6}>*/}
                {/*  <MDInput*/}
                {/*    label="Discount"*/}
                {/*    name="discount"*/}
                {/*    value={comboData.discount}*/}
                {/*    onChange={handleChange}*/}
                {/*    type="number"*/}
                {/*    fullWidth*/}
                {/*  />*/}
                {/*</Grid>*/}
              </Grid>
              <Grid item xs={12} sx={{ mt: "15px" }}>
                <MDInput
                  label="Transport"
                  value={comboData.transport}
                  onChange={(e) => {
                    const val = Math.max(0, parseInt(e.target.value) || 0);
                    handleTransportChange(val);
                  }}
                  type="number"
                  fullWidth
                />
              </Grid>
              {quotationType === "combo" && (
                <Grid item xs={12} sx={{ mt: "15px" }}>
                  <MDInput
                    label="Discount Amount (₹)"
                    value={discountAmount}
                    onChange={(e) => {
                      const val = Math.max(0, parseFloat(e.target.value) || 0);
                      setDiscountAmount(val);
                      calculateTotal(comboData.items);
                    }}
                    type="number"
                    fullWidth
                  />
                </Grid>
              )}
              <Grid item xs={12} sx={{ mt: "15px" }}>
                <MDInput
                  label="Customer PO Number (optional)"
                  name="customerPoNumber"
                  value={comboData.customerPoNumber || ""}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <MDBox mt={2} fontWeight="bold">
                  {quotationType === "combo" ? (
                    <>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Subtotal: ₹{" "}
                        {calculateComboItemsWithMargin(comboData.items, marginPercentage)
                          .reduce((sum, item) => {
                            const quantity = item.quantity ?? 1;
                            const price = parseFloat(item.sellingPrice) || 0;
                            return sum + price * quantity;
                          }, 0)
                          .toFixed(2)}
                      </Typography>
                      {discountAmount > 0 && (
                        <Typography variant="body2">
                          - Discount: ₹ {discountAmount.toFixed(2)}
                        </Typography>
                      )}
                      {comboData.transport && (
                        <Typography variant="body2">
                          + Transport: ₹ {parseFloat(comboData.transport).toFixed(2)}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Subtotal (after discount + transport): ₹{" "}
                        {(
                          calculateComboItemsWithMargin(comboData.items, marginPercentage).reduce(
                            (sum, item) => {
                              const quantity = item.quantity ?? 1;
                              const price = parseFloat(item.sellingPrice) || 0;
                              return sum + price * quantity;
                            },
                            0
                          ) -
                          discountAmount +
                          parseFloat(comboData.transport || 0)
                        ).toFixed(2)}
                      </Typography>
                      <Typography variant="body2">GST (18%): ₹ {totalTax}</Typography>
                      <Typography
                        variant="h6"
                        sx={{ mt: 1, fontWeight: "bold", color: "primary.main" }}
                      >
                        Final Total: ₹ {grandTotal}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Total Amount: ₹ {grandTotal}
                      </Typography>
                      <Typography variant="body2">
                        (excl. Tax: ₹ {(grandTotal - totalTax).toFixed(2)})
                      </Typography>
                      <Typography variant="body2"> GST: ₹ {totalTax} </Typography>
                    </>
                  )}
                </MDBox>
              </Grid>
              {/* <Grid item xs={12}>
                <MDBox mt={2} fontWeight="bold">
                  Total Amount: ₹ {grandTotal} <br />
                  <small> (excl. Tax: ₹ {grandTotal - totalTax} )</small>
                </MDBox>
              </Grid> */}

              <Grid
                container
                justifyContent="flex-end"
                pt={4}
                spacing={1}
                display="flex"
                sx={{
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { xs: "flex-end", md: "center" },
                }}
              >
                <Grid item pl={2} pr={2} xs="auto">
                  <MDButton variant="gradient" color="warning" onClick={handleCancel}>
                    Cancel
                  </MDButton>
                </Grid>
                <Grid item xs="auto">
                  <MDButton variant="gradient" color="info" onClick={handleSubmit}>
                    {id ? "Update Quotation" : "Create Quotation"}
                  </MDButton>
                </Grid>
                <Grid item xs="auto">
                  {id && (
                    <MDButton variant="gradient" color="info" onClick={() => sendInvoice(id)}>
                      Download Draft
                    </MDButton>
                  )}
                </Grid>
              </Grid>

              <AddressModal
                open={showAddressModal && editingAddressMode}
                onClose={() => {
                  setShowAddressModal(false);
                  setEditingAddressMode(false);
                }}
                onSave={handleAddressSaved}
                customerId={customer?.id}
                address={null}
              />

              {error && (
                <MDBox mt={2} color="error">
                  <strong>{error}</strong>
                </MDBox>
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default QuotationEdit;
